/**
 * The account menu (lane IA): everything that is not one of the five primary entries. Signed in it hangs off the
 * avatar (WalletButton); signed out a "More" button in the top bar opens the same reference links.
 */
import { useEffect, useRef, useState } from 'react'
import { Icon, IconButton, ThemeToggle, UiLink, cx } from '@algoboo/ui'
import { ACCOUNT, ADMIN_ITEM, LEGAL, MORE, type NavItem } from '../lib/nav'

const row = 'flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-[13.5px] text-ink-2 hover:bg-sunken hover:text-ink md:min-h-9 md:py-1.5'

function Item({ item, onClose }: { item: NavItem; onClose: () => void }) {
  return (
    <UiLink href={item.to} role="menuitem" onClick={onClose} className={row}>
      <Icon name={item.icon} size={15} className="shrink-0 text-ink-3" /> {item.label}
    </UiLink>
  )
}

/** Menu body: the account entries (signed in), the reference pages, legal links and the theme. */
export function AccountMenuItems({ signedIn, isAdmin, onClose }: { signedIn: boolean; isAdmin: boolean; onClose: () => void }) {
  return (
    <>
      {signedIn && (
        <>
          {[...ACCOUNT, ...(isAdmin ? [ADMIN_ITEM] : [])].map((i) => (
            <Item key={i.to} item={i} onClose={onClose} />
          ))}
          <div className="my-1 h-px bg-line" />
        </>
      )}
      {MORE.map((i) => (
        <Item key={i.to} item={i} onClose={onClose} />
      ))}
      <div className="my-1 h-px bg-line" />
      <div className="flex flex-wrap gap-x-3 px-3 py-1 text-[12px] text-ink-3">
        {LEGAL.map((l) => (
          <UiLink key={l.to} href={l.to} role="menuitem" onClick={onClose} className="inline-flex min-h-11 items-center hover:text-ink md:min-h-7">
            {l.label}
          </UiLink>
        ))}
      </div>
      <div className="flex items-center justify-between px-3 py-1 text-[13px] text-ink-3">
        Theme <ThemeToggle />
      </div>
    </>
  )
}

/** Signed out: the reference pages behind a "More" button next to Connect. */
export function MoreMenu({ className }: { className?: string }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <div ref={ref} className={cx('relative', className)}>
      <IconButton icon="menu" label="More" size={40} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)} />
      {open && (
        <div role="menu" aria-label="More" className="absolute right-0 top-11 z-40 max-h-[calc(100dvh-72px)] w-[min(15rem,calc(100vw-2rem))] overflow-y-auto overscroll-contain rounded-lg border border-line bg-surface p-1 shadow-float">
          <AccountMenuItems signedIn={false} isAdmin={false} onClose={() => setOpen(false)} />
        </div>
      )}
    </div>
  )
}
